import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const LockIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    style={{ width: '18px', height: '18px', marginRight: '8px', display: 'inline-block', verticalAlign: 'middle' }}
  >
    <path
      fillRule="evenodd"
      d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
      clipRule="evenodd"
    />
  </svg>
);

const SpinnerIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    style={{ width: '16px', height: '16px', marginRight: '8px', display: 'inline-block' }}
    className="animate-spin"
  >
    <style>{`.animate-spin { animation: spin 1s linear infinite; } @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: 0.3 }} />
    <path
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const ExclamationTriangleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    style={{ width: '16px', height: '16px', marginRight: '6px', display: 'inline-block' }}
  >
    <path
      fillRule="evenodd"
      d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
      clipRule="evenodd"
    />
  </svg>
);

interface LockBannerProps {
  nombreUsuario: string | null;
  bloqueadoDesde: Date | null;
  intentarTomarControl: () => Promise<boolean>;
}

export function LockBanner({ nombreUsuario, bloqueadoDesde, intentarTomarControl }: LockBannerProps) {
  const { t } = useTranslation();
  const [isTakingControl, setIsTakingControl] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTakeControlClick = async () => {
    setIsTakingControl(true);
    setError(null);
    const success = await intentarTomarControl();
    if (!success) {
      setError(t('lockBanner.takeControlError', { user: nombreUsuario || t('lockBanner.otherUser') }));
    }
    setIsTakingControl(false);
  };

  let tiempoTexto = '';
  if (bloqueadoDesde) {
    const diffMs = new Date().getTime() - bloqueadoDesde.getTime();
    const tiempoTranscurrido = Math.round(diffMs / 60000);
    if (tiempoTranscurrido < 1) {
      tiempoTexto = t('lockBanner.lessThanAMinute');
    } else {
      tiempoTexto = t('lockBanner.minutesAgo', { count: tiempoTranscurrido });
    }
  }

  return (
    <div className="alert alertWarning" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
      <LockIcon />
      <div style={{ flex: 1 }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', margin: '0 0 8px 0' }}>{t('lockBanner.readOnlyTitle')}</h3>
        <p style={{ margin: '0 0 4px 0' }}>
          <strong>{nombreUsuario || t('lockBanner.otherUser')}</strong> {t('lockBanner.isEditing')}
        </p>
        {bloqueadoDesde && <p style={{ margin: 0, fontSize: '0.9em', opacity: 0.8 }}>{t('lockBanner.lockedSince', { time: tiempoTexto })}</p>}

        <div style={{ marginTop: '16px' }}>
          <button onClick={handleTakeControlClick} disabled={isTakingControl} className="btn btnSecondary">
            {isTakingControl ? (
              <>
                <SpinnerIcon />
                {t('lockBanner.takingControl')}
              </>
            ) : (
              t('lockBanner.takeControl')
            )}
          </button>
        </div>
        {error && (
          <div style={{ marginTop: '12px', color: '#c00', fontWeight: '600', fontSize: '1em' }}>
            <ExclamationTriangleIcon />
            {error}
          </div>
        )}
      </div>
    </div>
  );
}